import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
//
import { StorageService, TranslateService, RedirectService } from '@services';
//
import { CommonSubjectStatus, CommonApiGetListOrderParams } from '@common2/common.types';
import { CommonThSortType } from '@common2/components/table-th/th-sort/th-sort.component';
import { UuidPipe } from '@common2/pipes/uuid.pipe';
import { ComponentTableModel } from '@models/component-table.model';
//
export interface OrganizeTableMarketplaceCatalogueDatum {
    category: Array<string>;
    courseId?: number | null;
    date: Date | string;
    icon: string;
    id: number;
    isDraft: boolean;
    isSelected: boolean;
    languageId: number;
    languageLabel: string;
    title: string;
    type: string;
    typeLabel: string;
}

@Component({
    selector: 'msc-organize-table-marketplace-catalogue-component',
    templateUrl: './organize-table-marketplace-catalogue.component.html',
    styleUrls: ['./organize-table-marketplace-catalogue.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [UuidPipe],
})

export class OrganizeTableMarketplaceCatalogueComponent extends ComponentTableModel<OrganizeTableMarketplaceCatalogueDatum> {
    @Input() rows: Array<OrganizeTableMarketplaceCatalogueDatum>;
    @Input() status: CommonSubjectStatus;
    @Input() filtersApplied: boolean = false;
    @Output() deleteEvent: EventEmitter<number> = new EventEmitter<number>();
    @Output() changeModeEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() sortEvent: EventEmitter<CommonApiGetListOrderParams> = new EventEmitter<CommonApiGetListOrderParams>();
    @Output() updateEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() selectAllEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() openModal: EventEmitter<any> = new EventEmitter<any>();

    private company: any;
    public id: string = this.uuidPipe.transform();

    constructor(
        protected readonly storageService: StorageService,
        protected readonly translateService: TranslateService,
        protected readonly uuidPipe: UuidPipe,
        protected readonly redirectService: RedirectService,
    ) {
        super();
    }

    get thCheckboxIsPartial(): boolean { return this.isThCheckboxPartial(); }
    get thCheckboxIsSelected(): boolean { return this.isThCheckboxSelected(); }

    ngOnInit(): void {
        this.company = this.storageService.get('company');
        this.setContent();
    }

    /**
     * Set the table component content
     */
    setContent(): void {
        this.columns = this.getColumns();
    }

    /**
     * Get the column list
     * Note: Order of columns is important
     */
    getColumns() {
        return [
            {
                key: 'isSelected',
                width: '5%',
            },
            {
                key: 'icon',
                width: '5%',
            },
            {
                key: 'isDraft',
                width: '5%',
            },
            {
                key: 'title',
                label: this.translateService.instant('organize.marketplace.catalogue.table.th.title'),
                width: '20%',
            },
            {
                key: 'type',
                label: this.translateService.instant('organize.marketplace.catalogue.table.th.type'),
                width: '10%',
            },
            {
                key: 'language',
                label: this.translateService.instant('words.language'),
                width: '15%',
            },
            {
                key: 'category',
                label: this.translateService.instant('organize.marketplace.catalogue.table.th.category'),
                width: '15%',
            },
            {
                key: 'last_edited', // key used by api sort
                label: this.translateService.instant('organize.marketplace.catalogue.table.th.date'),
                width: '15%',
            },
            {
                key: '',
                width: '10%',
            }
        ];
    }

    /**
     * Resolves the check if thead checkbox cell is partial or not
     */
    isThCheckboxPartial(): boolean {
        return !this.rows.every((row, _i, rows) => rows[0].isSelected === row.isSelected);
    }

    /**
     * Resolves the check if thead checkbox is selected fully or not
     */
    isThCheckboxSelected(): boolean {
        return this.rows.every((row) => row.isSelected);
    }

    /**
     * Event handler for change
     */
    onChange(value: boolean, datum: OrganizeTableMarketplaceCatalogueDatum): void {
        datum.isSelected = value;
        this.updateEvent.emit(this.rows);
    }

    /**
     * Event handler for modify
     */
    onModify(value: boolean): void {
        this.rows.forEach((row) => row.isSelected = value);
        this.selectAllEvent.emit(value);
        this.updateEvent.emit(this.rows);
    }

    /**
     * Event handler for delete of rows
     */
    onDelete(id: number): void {
        this.deleteEvent.emit(id);
    }

    /**
     *
     */
    onChangeMode(status: string, id: number): void {
        this.changeModeEvent.emit({ status, id });
    }

    /**
     * Event handler for sort
     */
    onSort(key: keyof OrganizeTableMarketplaceCatalogueDatum, type: CommonThSortType): void {
        this.sortEvent.emit({ order: type, sort: key });
    }

    /**
     * Event handler for open modal
     */
    onOpenModal(): void {
        this.openModal.emit();
    }

    /**
     * Event handler for redirect to create
     */
    onGoToCreate() {
        this.redirectService.navigate('studio', ['company', this.company.slug], {}, '_blank');
    }
}
